import { useState, useEffect } from "react";
import { C } from "../lib/colors";
import { Logo } from "../components/Logo";
import { requestPasswordReset } from "../lib/auth";
import { MinimalFooter } from "../components/MinimalFooter";

// Cooldown between resend attempts. Long enough to discourage spam (and
// Supabase auth rate limits), short enough that someone who genuinely didn't
// get the email isn't stuck waiting forever.
const RESEND_COOLDOWN_SEC = 60;

export const ForgotPasswordPage = ({ onNavigate }) => {
  const [email,   setEmail]   = useState("");
  const [loading, setLoading] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [error,   setError]   = useState("");
  // ↻ Resend state: when the cooldown ends (epoch ms), how many seconds remain,
  // and the small "✓ Sent again" flash after each successful resend.
  const [cooldownEndsAt, setCooldownEndsAt] = useState(0);
  const [secondsLeft,    setSecondsLeft]    = useState(0);
  const [resendError,    setResendError]    = useState("");
  const [resentFlash,    setResentFlash]    = useState(false);

  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim());

  // Countdown ticker — updates the "Resend in 45s" label once per second while
  // a cooldown is active. Stops when the cooldown is over to avoid useless work.
  useEffect(() => {
    if (cooldownEndsAt <= 0) return undefined;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((cooldownEndsAt - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining === 0) setCooldownEndsAt(0);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [cooldownEndsAt]);

  const handleSubmit = async () => {
    if (!isValidEmail(email) || loading) return;
    setLoading(true); setError("");
    try {
      await requestPasswordReset(email);
      setSent(true);
      // Start the resend cooldown immediately after the first send too.
      setCooldownEndsAt(Date.now() + RESEND_COOLDOWN_SEC * 1000);
    } catch (err) {
      setError(err.message || "Could not send reset email.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (secondsLeft > 0 || loading) return;
    setLoading(true); setResendError(""); setResentFlash(false);
    try {
      await requestPasswordReset(email);
      setResentFlash(true);
      setCooldownEndsAt(Date.now() + RESEND_COOLDOWN_SEC * 1000);
      // Flash the success indicator for 3s then quietly fade it.
      setTimeout(() => setResentFlash(false), 3000);
    } catch (err) {
      const msg = err.message || "Could not resend.";
      // Friendlier copy for rate-limit errors which are the most common
      // failure mode here — Supabase throttles password resets aggressively.
      setResendError(/rate.?limit|too many/i.test(msg)
        ? "Too many attempts. Wait a minute and try again."
        : msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{minHeight:"100vh",background:"#000000",fontFamily:"'Instrument Sans',sans-serif",color:C.text,
      display:"flex",flexDirection:"column"}}>
      <nav style={{padding:"0 5%",height:96,display:"flex",alignItems:"center",justifyContent:"space-between",
        borderBottom:`1px solid ${C.border}`}}>
        <button onClick={()=>onNavigate("landing")} style={{background:"none",border:"none",cursor:"pointer"}}>
          <Logo size={44}/>
        </button>
        <button onClick={()=>onNavigate("login")} style={{background:"none",border:"none",color:C.muted,
          fontSize:13,cursor:"pointer",fontFamily:"'Instrument Sans',sans-serif"}}>
          ← Back to login
        </button>
      </nav>

      <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
        <div style={{width:"100%",maxWidth:420,animation:"fadeUp 0.4s ease"}}>

          {sent ? (
            <div style={{textAlign:"center"}}>
              <div style={{width:64,height:64,margin:"0 auto 18px",borderRadius:"50%",
                background:`${C.green}15`,border:`2px solid ${C.green}`,
                display:"flex",alignItems:"center",justifyContent:"center",fontSize:28}}>✓</div>
              <h1 style={{fontFamily:"'Sora',sans-serif",fontSize:24,fontWeight:800,letterSpacing:-1,marginBottom:10}}>
                Check your inbox
              </h1>
              <p style={{color:C.muted,fontSize:14,lineHeight:1.65,marginBottom:18}}>
                If an account exists for <strong style={{color:C.green}}>{email}</strong>, we've sent a password reset link.
                Click it and you'll be able to choose a new password.
              </p>
              <p style={{color:C.muted,fontSize:12,lineHeight:1.65,marginBottom:18}}>
                Didn't receive the email within a couple of minutes? Check your spam folder, or resend the link below.
              </p>

              {/* Resend control + cooldown countdown */}
              <div style={{
                background:"rgba(255,255,255,0.03)", border:`1px solid ${C.border}`, borderRadius:12,
                padding:"14px 16px", marginBottom:18, display:"flex", flexDirection:"column",
                alignItems:"center", gap:8,
              }}>
                <button onClick={handleResend} disabled={loading || secondsLeft > 0}
                  style={{
                    background: loading || secondsLeft > 0 ? "rgba(255,255,255,0.05)" : `${C.green}18`,
                    color:      loading || secondsLeft > 0 ? C.muted               : C.green,
                    border:     `1px solid ${loading || secondsLeft > 0 ? C.border : `${C.green}55`}`,
                    borderRadius:10, padding:"10px 18px", fontSize:13, fontWeight:700,
                    cursor: loading || secondsLeft > 0 ? "not-allowed" : "pointer",
                    fontFamily:"'Instrument Sans',sans-serif",
                    display:"flex", alignItems:"center", gap:8,
                  }}>
                  {loading
                    ? <><span style={{width:12,height:12,border:`2px solid ${C.muted}`,borderTopColor:C.green,
                        borderRadius:"50%",display:"inline-block",animation:"spin 0.7s linear infinite"}}/>Sending…</>
                    : secondsLeft > 0
                      ? `↻ Resend in ${secondsLeft}s`
                      : "↻ Resend reset link"}
                </button>
                {resentFlash && (
                  <div style={{fontSize:12, color:C.green, display:"flex", alignItems:"center", gap:6}}>
                    <span>✓</span> Sent again — check your inbox.
                  </div>
                )}
                {resendError && (
                  <div style={{fontSize:12, color:C.red, display:"flex", alignItems:"center", gap:6}}>
                    <span>⚠</span> {resendError}
                  </div>
                )}
              </div>

              <button onClick={()=>onNavigate("login")} style={{background:"transparent",color:C.text,
                border:`1px solid ${C.border}`,borderRadius:10,padding:"11px 24px",fontSize:13,fontWeight:600,
                cursor:"pointer",fontFamily:"'Instrument Sans',sans-serif"}}>
                Back to login
              </button>
            </div>
          ) : (
            <>
              <div style={{textAlign:"center",marginBottom:32}}>
                <div style={{display:"inline-flex",alignItems:"center",justifyContent:"center",
                  width:56,height:56,borderRadius:16,background:`linear-gradient(135deg,${C.green},${C.blue})`,
                  fontSize:24,marginBottom:18}}>🔑</div>
                <h1 style={{fontFamily:"'Sora',sans-serif",fontSize:26,fontWeight:800,letterSpacing:-1,marginBottom:8}}>
                  Forgot your password?
                </h1>
                <p style={{color:C.muted,fontSize:14,lineHeight:1.6}}>
                  Enter your account email and we'll send you a link to set a new password.
                </p>
              </div>

              <div style={{background:C.s1,border:`1px solid ${C.border}`,borderRadius:18,padding:28}}>
                <div style={{marginBottom:18}}>
                  <div style={{fontSize:12,fontWeight:600,marginBottom:7,color:"rgba(255,255,255,0.7)"}}>
                    Email address
                  </div>
                  <input value={email} onChange={e=>setEmail(e.target.value)}
                    onKeyDown={e=>e.key==="Enter"&&handleSubmit()}
                    placeholder="you@yourbusiness.com" type="email" autoComplete="email"
                    style={{width:"100%",background:C.s2,border:`1.5px solid ${C.border}`,borderRadius:10,
                      padding:"12px 14px",color:C.text,fontSize:14,fontFamily:"'Instrument Sans',sans-serif",
                      transition:"border-color 0.2s"}}
                    onFocus={e=>e.target.style.borderColor=C.green}
                    onBlur={e=>e.target.style.borderColor=C.border}/>
                </div>

                {error && (
                  <div style={{color:C.red,fontSize:12,marginBottom:14,display:"flex",alignItems:"center",gap:6}}>
                    <span>⚠</span>{error}
                  </div>
                )}

                <button onClick={handleSubmit} disabled={loading||!isValidEmail(email)}
                  style={{width:"100%",background:loading||!isValidEmail(email)?"rgba(255,255,255,0.06)":C.green,
                    color:loading||!isValidEmail(email)?C.muted:"#000",border:"none",borderRadius:10,
                    padding:"13px",fontSize:14,fontWeight:700,
                    cursor:loading||!isValidEmail(email)?"not-allowed":"pointer",
                    fontFamily:"'Instrument Sans',sans-serif",
                    display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                  {loading
                    ? <><span style={{width:14,height:14,border:"2px solid rgba(0,0,0,0.2)",borderTopColor:"#000",
                        borderRadius:"50%",display:"inline-block",animation:"spin 0.7s linear infinite"}}/>Sending…</>
                    : "Send reset link →"}
                </button>
              </div>

              <div style={{textAlign:"center",marginTop:18,fontSize:13,color:C.muted}}>
                Remember it after all?{" "}
                <button onClick={()=>onNavigate("login")} style={{background:"none",border:"none",
                  color:C.green,cursor:"pointer",fontWeight:600,fontFamily:"'Instrument Sans',sans-serif",fontSize:13}}>
                  Back to login
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      <MinimalFooter onNavigate={onNavigate}/>
    </div>
  );
};
